import React, { useState } from 'react';
import { QueryParam, type Nullable } from 'corgi-types';
import { CartType } from 'graphql-ts-types';

import HomeClubSubscriptionModal from 'common/components/HomeClubSubscriptionModal';
import { CONFIRMATION_PATH } from 'common/constants/checkout';
import cookieStorage from 'common/CookieStorage';
import { executeQuery } from 'common/gql';
import reevaluateCartDiscountsMutation from 'common/gql/mutation/cart/reevaluate-cart-discount.gql';
import { useFormSubmit } from 'common/hooks/home-club/use-form-submit';
import { useGQLLocale } from 'common/hooks/locale';
import { useLocalization } from 'common/providers/LocaleProvider';
import { getMessageFromGqlError } from 'common/utils/error-handlers/gql-extensions';
import { CartSummaryEventName } from '../constants';

interface CheckoutHomeClubModalProps {
  isModalOpen: boolean;
  eventName: string;
  homeClubDiscount?: number;
}

const CheckoutHomeClubModal = ({
  isModalOpen,
  homeClubDiscount,
  eventName,
}: CheckoutHomeClubModalProps) => {
  const { t, prefix } = useLocalization();
  const locale = useGQLLocale();
  const buttonLabel = t('h24_hc_submit_btn_label');

  const [errorText, setErrorText] = useState<Nullable<string>>(null);

  const onSuccess = () => {
    window.location.href = `${prefix(CONFIRMATION_PATH)}&${QueryParam.HomeClubOptInSuccess}=true`;
  };

  const onSkipHomeClub = async () => {
    try {
      await executeQuery({
        query: reevaluateCartDiscountsMutation,
        variables: {
          locale,
          cartType: CartType.Default,
          zipCode: cookieStorage.getZipCode(),
        },
        useApq: true,
      });

      window.location.href = prefix(CONFIRMATION_PATH);
    } catch (error) {
      const message = getMessageFromGqlError(error, t);
      setErrorText(message);
    }
  };

  const { handleSubmit, isLoading } = useFormSubmit({
    tracking: {
      eventName: CartSummaryEventName.Click,
      title: 'checkout_home_club_modal',
      buttonLabel,
    },
    setErrorText,
    onSuccess,
    withSuccessDelay: true,
  });

  return (
    <HomeClubSubscriptionModal
      isModalOpen={isModalOpen}
      errorText={errorText}
      isLoading={isLoading}
      buttonLabel={buttonLabel}
      handleSubmit={handleSubmit}
      withDefaultHeader={false}
      trackingProps={{
        eventName,
        title: 'checkout_home_club_modal',
      }}
      homeClubDiscount={
        homeClubDiscount ? { amount: homeClubDiscount, onSkip: onSkipHomeClub } : undefined
      }
    />
  );
};

export default CheckoutHomeClubModal;
